import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Loader2 } from "lucide-react";

const FieldLabel = ({ children }) => (
  <label className="block text-[10px] uppercase tracking-[0.18em] font-semibold text-[#0A4730]/70 mb-2">
    {children}
  </label>
);

const INPUT_CLASS =
  "w-full rounded-2xl bg-white border border-slate-200 px-4 py-3.5 text-sm text-[#0A2540] placeholder:text-slate-400 focus:outline-none focus:border-[#0A4730] focus:ring-4 focus:ring-[#0A4730]/5 transition-all duration-200 shadow-sm";

export function LogInStep({ onForgot, onSubmit, loading, error }) {
  const navigate = useNavigate();
  const [usePhone, setUsePhone] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm();

  const handleFormSubmit = async (data) => {
    try {
      if (!onSubmit) {
        return;
      }
      await onSubmit({
        identifier: usePhone ? data.phone : data.email,
        password: data.password,
      });
    } catch {}
  };

  return (
    <div className="flex flex-col h-full w-screen md:w-full mx-auto bg-[#FDFDFD] p-5 md:p-8 text-left">
      <div className="mb-6 text-center">
        <h1 className="text-2xl text-[#0A4730] font-serif font-semibold tracking-tight">
          Login
        </h1>
      </div>

      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        className="space-y-6 flex-grow"
      >
        {/* Credentials Card */}
        <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)] space-y-4">
          {/* Modern Toggle Tabs */}
          <div>
            <FieldLabel>Sign in with</FieldLabel>
            <div className="grid grid-cols-2 p-1 bg-slate-100 rounded-2xl border border-slate-200/60 mb-4">
              <button
                type="button"
                onClick={() => {
                  setUsePhone(false);
                  setValue("phone", "");
                }}
                className={`py-2 text-xs font-semibold rounded-xl transition-all duration-200 ${
                  !usePhone
                    ? "bg-white text-[#0A4730] shadow-sm"
                    : "text-slate-500 hover:text-[#0A4730]"
                }`}
              >
                Email
              </button>
              <button
                type="button"
                onClick={() => {
                  setUsePhone(true);
                  setValue("email", "");
                }}
                className={`py-2 text-xs font-semibold rounded-xl transition-all duration-200 ${
                  usePhone
                    ? "bg-white text-[#0A4730] shadow-sm"
                    : "text-slate-500 hover:text-[#0A4730]"
                }`}
              >
                Phone Number
              </button>
            </div>
          </div>

          {!usePhone ? (
            <div>
              <FieldLabel>Email Address</FieldLabel>
              <input
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /\S+@\S+\.\S+/,
                    message: "Invalid email address format",
                  },
                })}
                placeholder="ex: jon.smith@email.com"
                className={INPUT_CLASS}
              />
              {errors.email && (
                <p className="text-xs text-red-500 mt-1.5">
                  {errors.email.message}
                </p>
              )}
            </div>
          ) : (
            <div>
              <FieldLabel>Phone Number</FieldLabel>
              <input
                {...register("phone", {
                  required: "Phone number is required",
                  pattern: {
                    value: /^\+?[1-9]\d{7,14}$/,
                    message: "Enter a valid phone number (e.g. +447700900123)",
                  },
                })}
                placeholder="ex: +447700900123"
                className={INPUT_CLASS}
              />
              {errors.phone && (
                <p className="text-xs text-red-500 mt-1.5">
                  {errors.phone.message}
                </p>
              )}
            </div>
          )}

          <div>
            <div className="flex items-center justify-between">
              <FieldLabel>Password</FieldLabel>
              <button
                type="button"
                onClick={onForgot}
                className="text-xs font-semibold text-[#0A4730] hover:underline mb-2"
              >
                Forgot Password?
              </button>
            </div>
            <input
              type="password"
              {...register("password", {
                required: "Password is required",
              })}
              placeholder="*********"
              className={INPUT_CLASS}
            />
            {errors.password && (
              <p className="text-xs text-red-500 mt-1.5">
                {errors.password.message}
              </p>
            )}
          </div>
        </div>

        {/* Backend error */}
        {error && (
          <div className="rounded-2xl bg-red-50 border border-red-100 p-4 text-red-500 text-xs">
            {error}
          </div>
        )}

        {/* Submit */}
        <div className="pt-2">
          <button
            type="submit"
            disabled={loading}
            className="w-full h-14 rounded-2xl bg-[#0A4730] text-white font-bold tracking-wide hover:bg-[#0A4730]/90 transition-all shadow-lg active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            {loading ? "Logging in..." : "Login"}
          </button>
        </div>
      </form>

      {/* Footer */}
      <div className="mt-8 text-center text-sm">
        <span className="text-slate-500">Don't have an account? </span>
        <button
          className="font-bold text-[#0A4730] hover:underline"
          onClick={() => navigate("/signup")}
        >
          Sign Up
        </button>
      </div>
    </div>
  );
}
